import { NavLink } from 'react-router-dom'
import {
  BarChart3,
  Bot,
  FolderOpen,
  Headphones,
  Home,
  MessageSquare,
  MessagesSquare,
  Settings,
  X,
  Globe
} from 'lucide-react'
import { cn } from '../utils/cn'
import { useTranslation } from '../i18n'
import { useAuth } from '../hooks/useAuth'

interface SidebarProps {
  closeSidebar?: () => void
}

export default function Sidebar({ closeSidebar }: SidebarProps) {
  const { t, locale, setLocale } = useTranslation()
  const { user } = useAuth()
  const role = user?.role || 'agent'
  const isAdmin = role === 'superadmin' || role === 'admin'

  const navigation = [
    // Admin-only pages
    ...(isAdmin ? [
      { name: t('nav.dashboard'), href: '/dashboard', icon: Home },
      { name: t('nav.projects'), href: '/projects', icon: FolderOpen },
      { name: t('nav.knowledgeBase'), href: '/knowledge', icon: Bot },
      { name: t('nav.conversations'), href: '/conversations', icon: MessagesSquare },
    ] : []),
    // Everyone can access agent workspace
    { name: t('nav.agentWorkspace'), href: '/agent', icon: Headphones },
    // Admin-only
    ...(isAdmin ? [
      { name: t('nav.analytics'), href: '/analytics', icon: BarChart3 },
    ] : []),
    // Superadmin only
    ...(role === 'superadmin' ? [
      { name: t('nav.settings'), href: '/settings', icon: Settings },
    ] : []),
  ]

  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4">
      <div className="flex h-16 shrink-0 items-center justify-between">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-8 w-8 text-indigo-400" />
          <h1 className="text-xl font-bold text-white">ClawCustomer</h1>
        </div>
        {closeSidebar && (
          <button
            type="button"
            className="lg:hidden -m-2.5 p-2.5 text-gray-400 hover:text-white"
            onClick={closeSidebar}
          >
            <span className="sr-only">{t('layout.closeSidebar')}</span>
            <X className="h-6 w-6" />
          </button>
        )}
      </div>
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => (
                <li key={item.href}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors',
                        isActive
                          ? 'bg-gray-800 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-gray-800'
                      )
                    }
                  >
                    <item.icon className="h-6 w-6 shrink-0" />
                    {item.name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>
          <li className="mt-auto">
            <button
              onClick={() => setLocale(locale === 'zh' ? 'en' : 'zh')}
              className="group flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <Globe className="h-6 w-6 shrink-0" />
              {locale === 'zh' ? t('layout.switchToEnglish') : t('layout.switchToChinese')}
            </button>
          </li>
        </ul>
      </nav>
    </div>
  )
}
